import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { ChevronRight } from "lucide-react";

import { usePayments } from "../../application/usePayments";
import { formatMoney } from "../../domain/types";
import PaymentStatusBadge from "./PaymentStatusBadge";

interface Props {
    sessionId: string;
}

/**
 * What was charged for this session, on the session page.
 *
 * Every status, not just the unpaid ones: the question a therapist opening a
 * session asks is "did I bill for this?", and a paid payment answers it as
 * well as a pending one. A cancelled one answers it too — it says the charge
 * was raised and withdrawn, which is not the same as never having billed.
 *
 * Renders nothing when the session was never charged for. A row saying "no
 * payment" would appear on every session a therapist does not bill
 * individually, which for most of them is most sessions.
 */
export default function SessionPaymentSummary({ sessionId }: Props) {
    const { t } = useTranslation();
    const { data, isLoading, isError } = usePayments({ sessionId, limit: 10 });

    // Silent on failure and while loading: this sits above the clinical notes,
    // and a spinner or an error there interrupts the thing the page is for.
    if (isLoading || isError || !data || data.items.length === 0) return null;

    return (
        <div className="mb-6 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
            <h3 className="mb-3 text-sm font-semibold text-content-primary">
                {t("payments.session.title", { count: data.items.length })}
            </h3>
            <ul className="space-y-2">
                {data.items.map((payment) => (
                    <li key={payment.id}>
                        <Link
                            to={`/payments/${payment.id}`}
                            className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-white/[0.03]"
                        >
                            <span className="min-w-0 flex-1 truncate text-sm text-content-secondary">
                                {payment.line_items[0]?.description ?? "—"}
                            </span>
                            <PaymentStatusBadge status={payment.status} />
                            <span className="shrink-0 text-sm font-semibold text-content-primary">
                                {formatMoney(payment.total)}
                            </span>
                            <ChevronRight className="h-4 w-4 shrink-0 text-content-tertiary" />
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}
